<script setup lang="ts" generic="T">
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    import Button from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import { convertFormData } from "@/components/forms/Form/convertFormData";
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import { trigger } from "@/components/forms/Form/trigger";
    import {
        GetFieldInitialValue,
        GetFieldValue,
        HasUnsavedData,
        ManualFormDataConvert,
        OnCancel,
        SchemaTrigger,
        SetFieldInitialValue,
        SetFieldValue,
        SetFormValues,
        SetInitialValues,
        Trigger,
        ManualSubmit,
    } from "@/components/forms/Form/types";
    import { KFormExposeApi } from "@/components/forms/Form/types.d";
    import { registerForm, unregisterForm } from "@/components/forms/formRegistry";
    import { useStore } from "@/store";
    import { CONFIG_PROVIDE_NAME } from "@/store/modules/types/configModuleTypes";
    import cloneDeep from "lodash/cloneDeep";
    import isEqual from "lodash/isEqual";
    import emitter from "tiny-emitter/instance";
    // import { FormKit } from "@formkit/vue";
    // TODO T extends FOrmKitGroupValue för FormKit
    // import { FormKitGroupValue, FormKitNode, FormKitSchemaDefinition, reset } from "@formkit/core";
    // import Library from "@/components/forms/Form/library";
    import { computed, onMounted, onUnmounted, provide, ref, toRaw, toRefs, watch } from "vue";
    import { setFormReadonly } from "../displays/FormReadonly/FormReadonly";

    const store = useStore();

    /**
     * Sätt klassen "form-content" på container som innehåller delar av ett formulär.
     * Kan sättas på fler containers (t.ex innehållet i flera KSections som är inom samma KForm).
     *
     * Sätt klassen "form-section" på en container som ska innehålla en till flera formulärkomponenter. Den containern
     * kan också få någon av dessa klasser för att avgöra hur många kolumner sektionen kan sträcka ut sig över:
     * span-all: Upp till alla (4) kolumner
     * span-three: Upp till 3 kolumner
     * span-two: Upp till 2 kolumner
     *
     * Sätt någon av ovan klasser (span-all/span-three/span-two) på children inom en container med "form-section"
     * för att avgöra hur många kolumner inom sektionen som komponenten ska ta.
     *
     * T.ex:
     * En div med class "form-section span-three" kommer sträcka ut sig över 3 kolumner. En komponent i den
     * div:en med class "span-all" kommer att sträcka ut sig över alla kolumner inom sektionen, vilket är 3. Om div:en
     * hade haft "span-all" så hade child med "span-cell" sträckt sig över 4 kolumner.
     *
     * Sätt klassen "divider" på "form-section" för att rendera ut en divider (linje under det elementet)
     */

    export interface KFormEmits<T> {
        /**
         * on cancel.
         *
         * Target är namn på knapp/annat som aktiverats för att cancla formuläret.
         *
         * hasUnsavedData är en boolean som är true om data i formuläret förändrats
         */ (
            e: "cancel",
            data: {
                target?: string;
                hasUnsavedData: boolean;
            }
        ): void;

        // on submit. Node kommer att användas vid migrering till Formkit
        (e: "submit", formData: T, node: unknown): void;
        (e: "updated", formData: T): void;

        // TODO Formkit on submit. Använd node för att manipulera formuläret (t.ex error)
        // (e: "submit", formData: T, node: FormKitNode): void,
    }

    export interface KFormProps<T> {
        /**
         * Id för form
         */
        id: string;
        /**
         * Funktion som anropas för att hämta värden att sätta som default i form (t.ex i förifyllt form).
         * formOptions:
         * - skipAllTriggers: true gör så att inga triggers körs vid sättning av data (default är att det görs).
         * - skipTriggerOnFields tar emot array med namn på fält. triggers som har det fältet satt i dependentOn kommer
         * inte köras
         */
        initialValuesGetter?: () => Promise<Partial<
            T & { formOptions?: { skipAllTriggers?: boolean; skipTriggerOnFields?: string[] } }
        > | null>;
        /**
         * Innehållet i submit-knappen. Knappen kommer inte renderas om submitContent är undefined
         */
        submitContent?: string;
        /**
         * Innehållet i cancel-knappen. Knappen kommer inte renderas om cancelContent är undefined
         */
        cancelContent?: string;
        /**
         * Funktion som kommer anropas som del av submit-flödet. @submit kommer fortfarande emittas
         */
        submit?: (formData: T, node: unknown) => void;
        /**
         * Funktion som kommer anropas som del av cancel-flödet. @cancel kommer fortfarande emittas
         */
        cancel?: (props: { target?: string; hasUnsavedData: boolean }) => void;
        /**
         * Type ska aldrig sättas på form
         */
        type?: never;
        /**
         * Alignment på actionknapparna. Används om en custom template skickats in för actions
         */
        actionsAlign?: "left" | "center" | "right";
        /**
         * Form schema
         */
        schema?: unknown;
        /**
         * Antal kolumner i grid
         */
        columns?: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
        /**
         * Ska tidigare inamtad data i formuläret visas som förslag när användaren skriver in data i samma fält?
         * Det här ska 99.99% av gångerna sättas till false. false är default
         */
        autocomplete?: boolean;
        /**
         * Namn på config. Används för att hämta inställningar
         */
        configModuleName?: string;
        /**
         * Namn på config. Används för att hämta inställningar
         */
        configSubModuleName?: string;
        /**
         * Samling med fält som ska lyssna på ändringar på andra fält och göra något baserat på ändring
         */
        triggers?: Array<Trigger>;
        /**
         * Triggers som skickas in från schema
         */
        schemaTriggers?: Array<SchemaTrigger>;
        /**
         * Samling av fält som ska modifiera sina värden innan det skickas till backend
         */
        converters?: Array<{
            fieldName: string; // Namn på fältet
            converter: ({ value }: { value: unknown | unknown[] }) => unknown | unknown[]; // Funktion som returnerar förändrade värdet
        }>;
        /**
         * Lyssnar på ändringar på ett eller flera fält.
         * fieldNames är namnen på fälten du vill lyssna på ändringar på
         * action är funktionen som tar emot ett objekt som innehåller värdet på alla fält som lyssnats på
         * (t.ex { falt1somlyssnaspå: true, falt2somlyssnaspå: 57 })
         */
        listeners?: Array<{
            fieldNames: string[];
            runAtInitial?: boolean;
            action: ({ values }: { values: { [key: string]: unknown } }) => void | Promise<void>;
            alwaysRunAtInitial?: boolean;
        }>;
        /**
         * Ska default submitknappen vara disabled?
         */
        disableSubmit?: boolean;
        /**
         * Button.vue size prop för default submit button
         */
        submitSize?: string;
        /**
         * Button.vue variant prop för default submit button
         */
        submitVariant?: string;
        /**
         * Button.vue size prop för default cancel button
         */
        cancelSize?: string;
        /**
         * Button.vue variant prop för default cancel button
         */
        cancelVariant?: string;
        /**
            Mängd padding på element som har class form-section. Kan användas för att kunna rendera avskiljare och
            ha padding på resten av innehållet i formuläret. Se Vardplatskalender och användning i CalendarFilter
         */
        sectionPadding?: string;
        /**
         * Ska hela formuläret vara readonly? Påverkar alla registrerade komponenter inom denna
         */
        readonly?: boolean;
        showEmptyValues?: boolean;
        /**
         * Visuella varianter för formuläret. Kan påverka max bredd och annat
         */
        variant?: "standard" | "dialog";
        /**
         * Visuella varianter för actions
         */
        actionsVariant?: "default" | "slim";
        /**
         * Mängd padding på hela formuläret. formPadding: sections kan användas för att justera ALLT innehåll så att
         * det är anpassat för numrerade KSections
         */
        formPadding?: "default" | "sections";
    }

    const formData = ref<Record<string, any>>({});
    const formRef = ref();
    /**
     * Formulärdata som använd som grund vid check om något förändrats i formuläret
     */
    const initialFormData = ref<Record<string, any>>({});
    /**
     * Kan sättas via setFormValues. Om det är true kommer nästa körning av triggers agera som om det vore första
     * körningen (initial)
     */
    const triggerAsInitial = ref(false);
    const convertedTriggers = computed<Trigger[]>(() => {
        const triggersFromSchema =
            schemaTriggers?.value?.map((trigger) => {
                const { fieldName, dependentOn, conditions } = trigger;

                return {
                    fieldName,
                    dependentOn,
                    action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                        const foundCondition = conditions.find(
                            (condition) =>
                                dependentOnValue === condition.dependentOnValue &&
                                (!condition.value || value === condition.value)
                        );

                        if (foundCondition) {
                            return {
                                ...foundCondition.result,
                                value:
                                    foundCondition.result.value || foundCondition.result.value === null
                                        ? foundCondition.result.value
                                        : value,
                            };
                        }

                        return {};
                    },
                };
            }) ?? [];
        return [...(triggers?.value ?? []), ...triggersFromSchema];
    });
    const emit = defineEmits<KFormEmits<T>>();
    // TODO Används defineModel i Vue3
    // const formData = defineModel<T>();

    const setFormValues: SetFormValues = ({ values, reset, triggerAsInitial: newTriggerAsInitial }) => {
        if (newTriggerAsInitial) {
            triggerAsInitial.value = newTriggerAsInitial;
        }

        if (reset) {
            formData.value = values;

            return;
        }

        formData.value = {
            ...formData.value,
            ...values,
        };
    };

    /**
     * Sätt värde på ett fält i forumuläret
     */
    const setFieldValue: SetFieldValue = ({ name, value }) => {
        formData.value = {
            ...formData.value,
            [name]: value,
        };
    };

    const getFieldValue: GetFieldValue = ({ name }) => {
        return formData.value[name];
    };

    const getFieldInitialValue: GetFieldInitialValue = ({ name }) => {
        return initialFormData.value[name];
    };

    const setFieldInitialValue: SetFieldInitialValue = ({ name, value }) => {
        initialFormData.value = {
            ...initialFormData.value,
            [name]: value,
        };
    };

    const setInitialValues: SetInitialValues = async ({ getter }) => {
        const getterData = await getter();

        if (getterData) {
            const { formOptions, ...newValues } = getterData;
            const filteredNewValues = Object.fromEntries(
                Object.entries(newValues).filter(([key]) => {
                    // Värde ska inte sättas på fält som är dolda via inställningar
                    return store.getters["config/shouldBeVisibleByName"]({
                        name: key,
                        moduleName: configModuleName?.value,
                        subModuleName: configSubModuleName?.value,
                    });
                })
            );

            const oldValues = cloneDeep(formData?.value);

            setFormValues({
                values: filteredNewValues,
                reset: false,
            });

            if (!formOptions?.skipAllTriggers) {
                runTriggers({
                    newValues: filteredNewValues as Partial<T>,
                    oldValues: oldValues as Partial<T>,
                    skipTriggerOnFields: formOptions?.skipTriggerOnFields,
                    initial: true,
                });
            }

            runListeners({ newValues: filteredNewValues, oldValues, initial: true });

            initialFormData.value = cloneDeep(formData.value);
        }
    };

    const getFormData = () => {
        return cloneDeep(formData.value);
    };

    /**
     * Sätt värde null på fält i initial och form data. Kan användas för fält som kan visas/döljas automatiskt och
     * se till att de vid osparad check har samma värde
     */
    const nullFieldValues = ({ fields }: { fields: string[] }) => {
        const values = fields.map((field) => [field, null]);

        initialFormData.value = {
            ...initialFormData.value,
            ...Object.fromEntries(values),
        };

        formData.value = {
            ...formData.value,
            ...Object.fromEntries(values),
        };
    };

    const props = withDefaults(defineProps<KFormProps<T>>(), {
        initialValuesGetter: undefined,
        submitContent: undefined,
        cancelContent: undefined,
        submit: undefined,
        cancel: undefined,
        schema: undefined,
        actionsAlign: undefined,
        columns: "4",
        autocomplete: false,
        triggers: undefined,
        schemaTriggers: undefined,
        converters: undefined,
        listeners: undefined,
        disableSubmit: false,
        submitSize: "primary",
        submitVariant: "standard",
        cancelVariant: "contour",
        cancelSize: "secondary",
        sectionPadding: "0",
        readonly: false,
        showEmptyValues: false,
        variant: "standard",
        actionsVariant: "default",
        formPadding: "default",
    });

    const {
        initialValuesGetter,
        id,
        submitContent,
        cancelContent,
        submit,
        cancel,
        actionsAlign,
        configModuleName,
        configSubModuleName,
        triggers,
        schemaTriggers,
        converters,
        listeners,
        readonly,
        columns,
        variant,
        showEmptyValues,
    } = toRefs(props);

    // Används för att stoppa onSubmit för att triggras vid manualSubmit
    const isManualSubmit = ref(false);

    /**
     * Gör namn på module och sub module tillgängligt till alla formulärkomponenter inom formuläret.
     * Används för att hämta inställningar från configModule i store
     */
    provide(CONFIG_PROVIDE_NAME, {
        moduleName: configModuleName?.value,
        subModuleName: configSubModuleName?.value,
    });

    provide("showEmptyValues", showEmptyValues.value);

    /**
     * Triggra submit av formuläret. Går igenom valideringsflödet som vanlig submit, men vår onSubmit anropas inte.
     * Returnerar false om formuläret har valideringsfel
     * @return form data eller false
     */
    const manualSubmit: ManualSubmit<T> = async (skipValidate = false) => {
        isManualSubmit.value = true;

        let data = null;

        if (skipValidate) {
            data = cloneDeep(formData.value);
        } else {
            data = await formRef.value.formSubmitted();
        }

        isManualSubmit.value = false;

        initialFormData.value = cloneDeep(formData.value);

        if (data) {
            return convertFormData({
                formData: data,
                registry: formRef.value.registry,
                converters: converters?.value,
            });
        }

        return data;
    };

    const hasUnsavedData: HasUnsavedData = () => {
        const initialFormState = JSON.stringify(
            convertFormData({
                formData: initialFormData?.value,
                registry: formRef.value.registry,
                converters: converters?.value,
            })
        );
        const copiedFormState = JSON.stringify(
            convertFormData({
                formData: formData?.value,
                registry: formRef.value.registry,
                converters: converters?.value,
            })
        );

        return initialFormState !== copiedFormState;
    };

    // FIXME Byt namn. Om funktionen kan anropas manuellt och inte bara via listener så borde den inte heta on*
    const onCancel: OnCancel = (event) => {
        const target = event?.target as HTMLButtonElement;

        cancel.value?.({
            target: target?.name,
            hasUnsavedData: hasUnsavedData(),
        });

        emit("cancel", {
            target: target?.name,
            hasUnsavedData: hasUnsavedData(),
        });
    };

    const manualFormDataConvert: ManualFormDataConvert = ({ data, displayMode } = {}) => {
        return convertFormData({
            displayMode,
            formData: data ?? formData.value,
            registry: formRef.value.registry,
            converters: converters?.value,
        });
    };

    const formExposeApi: KFormExposeApi = {
        onCancel,
        setFormValues,
        setFieldValue,
        setFieldInitialValue,
        manualSubmit,
        hasUnsavedData,
        setFormReadonly({ readonly }: { readonly: boolean }) {
            setFormReadonly({ registry: formRef.value.registry, readonly });
        },
        getFieldValue,
        getFieldInitialValue,
        manualFormDataConvert,
        nullFieldValues,
        getFormData,
    };

    defineExpose(formExposeApi);

    /**
     * on submit
     */
    const onSubmit = (submittedData: T & Object, node: unknown) => {
        if (isManualSubmit.value) {
            return;
        }

        // Konverterar datan
        const modifiedData = convertFormData({
            formData: submittedData,
            registry: formRef.value.registry,
            converters: converters?.value,
        });

        // Anropar funktion som skickats in som prop, om det gjorts
        submit.value?.(modifiedData, node);
        initialFormData.value = cloneDeep(formData.value);

        // TODO node kommer användas med Formkit
        emit("submit", modifiedData, node);
    };

    const runTriggers = ({
        newValues,
        oldValues,
        skipTriggerOnFields,
        initial,
    }: {
        newValues: Partial<T>;
        oldValues: Partial<T>;
        skipTriggerOnFields?: string[];
        initial?: boolean;
    }) => {
        if (convertedTriggers?.value) {
            (skipTriggerOnFields
                ? convertedTriggers.value.filter(({ dependentOn }) => {
                      if (Array.isArray(dependentOn)) {
                          return dependentOn.every((fieldName) => !skipTriggerOnFields.includes(fieldName));
                      }

                      return !skipTriggerOnFields.includes(dependentOn);
                  })
                : convertedTriggers.value
            ).forEach((props) => {
                trigger({
                    ...props,
                    newValues,
                    oldValues,
                    setFieldValue,
                    setFormValues,
                    setFieldInitialValue,
                    initial,
                    configModuleName: configModuleName?.value,
                    configSubModuleName: configSubModuleName?.value,
                    registry: formRef?.value?.registry,
                });
            });
        }
    };

    const runListeners = ({
        newValues,
        oldValues,
        initial,
    }: {
        newValues: { [key: string]: unknown };
        oldValues: { [key: string]: unknown };
        initial?: boolean;
    }) => {
        listeners?.value?.forEach(({ fieldNames, action, runAtInitial, alwaysRunAtInitial }) => {
            // Om det är init och det alltid ska köras på init
            // eller kolla om minst 1 av fälten som lyssnas på har ändrat värde
            if (
                (initial && alwaysRunAtInitial) ||
                ((!initial || runAtInitial) &&
                    fieldNames.some((fieldName) => !isEqual(newValues[fieldName], oldValues[fieldName])))
            ) {
                action({
                    // Anropar action med objekt med värde på fälten (key: namn på fältet)
                    values: Object.fromEntries(fieldNames.map((fieldName) => [fieldName, newValues[fieldName]])),
                });
            }
        });
    };

    const errorFields = ref<string[]>([]);
    provide("formErrorFields", errorFields);

    /**
     * Scrollar automatiskt till det översta fältet med valideringsfel. Det som skickas i är ett objekt med
     * key: namn på fältet
     */
    const onFailedValidation = (components: { [key: string]: Vue & { $el: Element } }) => {
        errorFields.value = Object.keys(components);
        const element = Object.entries(components)?.[0]?.[1]?.$el;

        if (element) {
            // Timeout behövs för att vänta tills KSection har hunnit öppnas (expandera kollapsade sektioner med fel)
            // innan vi scrollar till det första fältet med valideringsfel. Annars kan scrollen ske innan sektionen är synlig.
            setTimeout(() => {
                element.scrollIntoView({
                    inline: "center",
                    block: "center",
                    behavior: "smooth",
                });
            }, 200);
        }
    };

    provide(FORM_HELPERS, {
        setFieldInitialValue,
        setFormValues,
        setFieldValue,
        getFieldValue,
        manualFormDataConvert,
        hasUnsavedData,
    });

    const watchedFormData = computed(() => structuredClone(toRaw(formData.value)));

    onMounted(async () => {
        if (readonly?.value) {
            setFormReadonly({ registry: formRef.value?.registry?.registry, readonly: readonly.value });
        }

        /**
         * Registrerar formuläret i globalt formRegistry (gör exponerade funktioner globalt åtkomliga).
         */
        registerForm({ id: id.value, api: formExposeApi });
        /**
         * Sättning av förifylld data
         */
        if (formRef.value && typeof initialValuesGetter.value === "function") {
            await setInitialValues({ getter: initialValuesGetter.value });
        }

        initialFormData.value = cloneDeep(formData.value);
        emit("updated", initialFormData.value as T);

        /**
         * Kollar på förändringar i formulärets data och anropar triggers
         */
        watch(
            watchedFormData,
            async (newValues: { [key: string]: unknown }, oldValues: { [key: string]: unknown }) => {
                runTriggers({
                    newValues: newValues as Partial<T>,
                    oldValues: oldValues as Partial<T>,
                    initial: triggerAsInitial.value,
                });
                runListeners({ newValues, oldValues });

                emit("updated", formData.value as T);
                emitter.emit(`form-update-${id.value}`, { formData: formData.value as T });

                // Resettas så att nästa watch inte körs som initial, då triggerAsInitial är tänkt att bara sättas och
                // göra så att nästkommande körning av triggers räknas som initial
                if (triggerAsInitial.value) {
                    triggerAsInitial.value = false;
                }
            },
            {
                deep: true,
            }
        );
    });

    onUnmounted(() => {
        unregisterForm({ id: id.value });
    });
</script>
<template>
    <!--
        TODO Ändringar för formkit:
        outer-class till k-form
        name behövs inte
    -->
    <!--
        Tillgängliga slots:
            header (renderas högst upp)
            content (default. Innehållet i formuläret)
            actions (för submit, cancel och liknande. Har submit och cancel som default, om
                submitContent/cancelContent sätts
    -->
    <FormulateForm
        v-bind="{ ...$attrs, ...$props, ...$slots }"
        ref="formRef"
        :name="id"
        :id="id"
        :autocomplete="autocomplete ? 'on' : 'off'"
        :class="['k-form', `variant-${variant}`, `columns-${columns}`, `padding-${formPadding}`]"
        @submit="onSubmit"
        v-model="formData"
        @failed-validation="onFailedValidation"
    >
        <template v-if="$scopedSlots.header">
            <div class="form-header">
                <slot name="header" :formDataReference="formData" />
            </div>
        </template>
        <div class="form-content-wrapper">
            <slot />
        </div>
        <!-- TODO Använd <template #actions> med formkit -->
        <div
            v-if="submitContent || cancelContent || $slots?.actions"
            :class="['form-actions', `variant-${actionsVariant}`]"
        >
            <ButtonGroup v-if="submitContent || cancelContent || $slots?.actions" :align="actionsAlign" margin="none">
                <slot name="actions" :cancel="onCancel" :manualSubmit="manualSubmit" />
                <Button
                    v-if="submitContent"
                    :disabled="disableSubmit"
                    name="submit"
                    type="submit"
                    :size="submitSize"
                    :variant="submitVariant"
                >
                    {{ submitContent }}
                </Button>
                <Button
                    v-if="cancelContent"
                    name="cancel"
                    type="button"
                    :size="cancelSize"
                    :variant="cancelVariant"
                    @click="onCancel"
                >
                    {{ cancelContent }}
                </Button>
            </ButtonGroup>
        </div>
        <!-- </template> -->
    </FormulateForm>
</template>

<style scoped lang="scss">
    @use "sass:selector";
    @use "@/components/forms/Form/styles/FormContent";
    @import "@/components/forms/Form/styles/FormHeader";
    @import "@/components/forms/Form/styles/FormActions";
    @import "@/style/components";
    @import "@/style/container";
    @import "@/components/forms/Form/styles/mixins";

    .k-form {
        --section-padding: v-bind(sectionPadding);
        max-width: 100%;

        &.padding-sections {
            @include padding-sections;
        }

        &.variant-standard {
            width: $content-max-width;

            &.padding-sections {
                width: calc($content-max-width + $k-section-left-padding);
            }
        }

        &.variant-dialog {
            width: $dialog-wide-content-width;

            &.padding-sections {
                width: calc($dialog-wide-content-width + $k-section-left-padding);
            }
        }

        @include header;

        &.variant-standard {
            @include FormContent.columns(standard);
        }

        &.variant-dialog {
            @include FormContent.columns(dialog);
        }

        @include actions;
    }
</style>
